import type {
  ScheduleField,
  ScheduleTimeSlot,
} from "../shared/types/contracts";

export const WEATHER_TIME_ZONE = "America/Toronto";
export const WEATHER_FORECAST_DAYS = 16;

export const DINGER_THRESHOLD_KMH = 15;
export const DINGER_TOLERANCE_DEG = 30;

export interface WeatherCoordinates {
  latitude: number;
  longitude: number;
}

export interface PlateRelativeWind {
  speedKmh: number;
  directionDegrees: number;
}

export const SCHEDULE_FIELD_COORDINATES: Record<
  ScheduleField,
  WeatherCoordinates
> = {
  "Doug Wright": { latitude: 43.40848, longitude: -79.82346 },
  "Sherwood D2": { latitude: 43.38638, longitude: -79.75244 },
  "Sherwood D3": { latitude: 43.38638, longitude: -79.75244 },
  "Sherwood D4": { latitude: 43.38638, longitude: -79.75244 },
};

export const SCHEDULE_FIELD_BEARINGS: Record<ScheduleField, number> = {
  "Doug Wright": 355,
  "Sherwood D2": 175,
  "Sherwood D3": 88,
  "Sherwood D4": 180,
};

export function bearingForField(field: string | null): number | null {
  if (!field || !Object.hasOwn(SCHEDULE_FIELD_BEARINGS, field)) return null;
  return SCHEDULE_FIELD_BEARINGS[field as ScheduleField];
}

export interface WeatherHourData {
  time: string;
  temperature: number | null;
  apparentTemperature: number | null;
  precipitationProbability: number | null;
  precipitation: number | null;
  weatherCode: number | null;
  windSpeed: number | null;
  windGusts: number | null;
  windDirection: number | null;
}

export interface ForecastHour extends WeatherHourData {
  condition: string;
  periodStartMinute: number;
  periodEndMinute: number;
}

export interface GameForecast {
  startMinute: number;
  endMinute: number;
  condition: string;
  weatherCode: number | null;
  temperatureMin: number | null;
  temperatureMax: number | null;
  apparentTemperatureMin: number | null;
  apparentTemperatureMax: number | null;
  precipitationProbability: number | null;
  precipitation: number | null;
  dayPrecipitation: number | null;
  preGamePrecipitation: number | null;
  rainoutRisk: RainoutRisk | null;
  windSpeed: number | null;
  windGusts: number | null;
  hours: ForecastHour[];
}

export type RainoutRisk = "low" | "moderate" | "high";

export type ScheduleForecastState =
  | { status: "loading" }
  | { status: "available"; forecast: GameForecast }
  | { status: "out_of_range" }
  | { status: "unavailable" }
  | { status: "error" };

const SLOT_START_MINUTES: Record<ScheduleTimeSlot, number> = {
  early: 18 * 60 + 45,
  late: 20 * 60 + 45,
};

export function coordinatesForField(
  field: string | null,
): WeatherCoordinates | null {
  if (!field || !Object.hasOwn(SCHEDULE_FIELD_COORDINATES, field)) return null;
  return SCHEDULE_FIELD_COORDINATES[field as ScheduleField];
}

export function coordinateKey(coordinates: WeatherCoordinates): string {
  return `${coordinates.latitude},${coordinates.longitude}`;
}

export function gameWindowMinutes(
  timeSlot: string | null,
): { startMinute: number; endMinute: number } | null {
  if (timeSlot !== "early" && timeSlot !== "late") return null;
  const startMinute = SLOT_START_MINUTES[timeSlot];
  return { startMinute, endMinute: startMinute + 2 * 60 };
}

export function dateInToronto(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: WEATHER_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function calendarDayDistance(from: string, to: string): number | null {
  const fromTime = Date.parse(`${from}T00:00:00Z`);
  const toTime = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(fromTime) || !Number.isFinite(toTime)) return null;
  return Math.round((toTime - fromTime) / 86_400_000);
}

export function nowInTorontoMinutes(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: WEATHER_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0") % 24;
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return hour * 60 + minute;
}

export function findCurrentHour(
  hours: WeatherHourData[],
  now: Date = new Date(),
): WeatherHourData | null {
  const today = dateInToronto(now);
  const nowMinutes = nowInTorontoMinutes(now);
  const currentHourStart = Math.floor(nowMinutes / 60) * 60;
  const target = `${today}T${String(currentHourStart / 60).padStart(2, "0")}:00`;
  return hours.find((hour) => hour.time === target) ?? null;
}

export function angularDiff(a: number, b: number): number {
  let diff = (a - b) % 360;
  if (diff > 180) diff -= 360;
  else if (diff < -180) diff += 360;
  return diff;
}

export function plateRelativeWindDirection(
  windDirectionFrom: number | null,
  bearing: number,
): number | null {
  if (windDirectionFrom === null || !Number.isFinite(windDirectionFrom)) {
    return null;
  }
  const windDirectionToward = (windDirectionFrom + 180) % 360;
  return angularDiff(windDirectionToward, bearing);
}

interface DingerTimeInput {
  windDirectionFrom: number | null;
  windSpeed: number | null;
  bearing: number;
  thresholdKmh?: number;
  toleranceDeg?: number;
}

export function dingerConditions({
  windDirectionFrom,
  windSpeed,
  bearing,
  thresholdKmh = DINGER_THRESHOLD_KMH,
  toleranceDeg = DINGER_TOLERANCE_DEG,
}: DingerTimeInput): PlateRelativeWind | null {
  if (
    windSpeed === null ||
    !Number.isFinite(windSpeed) ||
    windSpeed < thresholdKmh
  ) {
    return null;
  }
  const directionDegrees = plateRelativeWindDirection(
    windDirectionFrom,
    bearing,
  );
  if (directionDegrees === null || Math.abs(directionDegrees) > toleranceDeg) {
    return null;
  }
  return { speedKmh: windSpeed, directionDegrees };
}

export function isDingerTime(input: DingerTimeInput): boolean {
  return dingerConditions(input) !== null;
}

export function buildGameForecast(
  scheduledDate: string,
  timeSlot: string | null,
  sourceHours: WeatherHourData[],
  dayPrecipitation: number | null = null,
): GameForecast | null {
  const window = gameWindowMinutes(timeSlot);
  if (!window) return null;
  const hours = sourceHours.flatMap((hour) => {
    const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(hour.time);
    if (!match || match[1] !== scheduledDate) return [];
    const hourEnd = Number(match[2]) * 60 + Number(match[3]);
    const periodStartMinute = Math.max(window.startMinute, hourEnd - 60);
    const periodEndMinute = Math.min(window.endMinute, hourEnd);
    if (periodStartMinute >= periodEndMinute) return [];
    return [
      {
        ...hour,
        condition: weatherCondition(hour.weatherCode),
        periodStartMinute,
        periodEndMinute,
      },
    ];
  });
  if (!hours.length) return null;

  const conditionHour = hours.reduce<ForecastHour | null>((selected, hour) => {
    if (hour.weatherCode === null) return selected;
    if (
      !selected ||
      weatherSeverity(hour.weatherCode) > weatherSeverity(selected.weatherCode)
    ) {
      return hour;
    }
    return selected;
  }, null);
  const precipitationValues = hours.filter(
    (hour) => hour.precipitation !== null,
  );
  const precipitation = precipitationValues.length
    ? precipitationValues.reduce(
        (total, hour) =>
          total +
          (hour.precipitation ?? 0) *
            ((hour.periodEndMinute - hour.periodStartMinute) / 60),
        0,
      )
    : null;
  const preGamePrecipitationValues = sourceHours.flatMap((hour) => {
    const hourEnd = localHourEnd(hour.time, scheduledDate);
    if (hourEnd === null || hour.precipitation === null) return [];
    const overlapStart = Math.max(0, hourEnd - 60);
    const overlapEnd = Math.min(window.startMinute, hourEnd);
    if (overlapStart >= overlapEnd) return [];
    return [hour.precipitation * ((overlapEnd - overlapStart) / 60)];
  });
  const preGamePrecipitation = preGamePrecipitationValues.length
    ? preGamePrecipitationValues.reduce((total, value) => total + value, 0)
    : null;
  const precipitationProbability = maximum(
    hours.map((hour) => hour.precipitationProbability),
  );
  const weatherCode = conditionHour?.weatherCode ?? null;

  return {
    ...window,
    condition: conditionHour?.condition ?? "Conditions unavailable",
    weatherCode,
    temperatureMin: minimum(hours.map((hour) => hour.temperature)),
    temperatureMax: maximum(hours.map((hour) => hour.temperature)),
    apparentTemperatureMin: minimum(
      hours.map((hour) => hour.apparentTemperature),
    ),
    apparentTemperatureMax: maximum(
      hours.map((hour) => hour.apparentTemperature),
    ),
    precipitationProbability,
    precipitation,
    dayPrecipitation,
    preGamePrecipitation,
    rainoutRisk: calculateRainoutRisk({
      precipitationProbability,
      gamePrecipitation: precipitation,
      preGamePrecipitation,
      weatherCode,
    }),
    windSpeed: maximum(hours.map((hour) => hour.windSpeed)),
    windGusts: maximum(hours.map((hour) => hour.windGusts)),
    hours,
  };
}

export function calculateRainoutRisk({
  precipitationProbability,
  gamePrecipitation,
  preGamePrecipitation,
  weatherCode,
}: {
  precipitationProbability: number | null;
  gamePrecipitation: number | null;
  preGamePrecipitation: number | null;
  weatherCode: number | null;
}): RainoutRisk | null {
  if (
    precipitationProbability === null &&
    gamePrecipitation === null &&
    preGamePrecipitation === null &&
    weatherCode === null
  ) {
    return null;
  }
  let score = 0;
  if ((precipitationProbability ?? 0) >= 70) score += 2;
  else if ((precipitationProbability ?? 0) >= 40) score += 1;
  if ((gamePrecipitation ?? 0) >= 4) score += 3;
  else if ((gamePrecipitation ?? 0) >= 1.5) score += 2;
  else if ((gamePrecipitation ?? 0) >= 0.3) score += 1;
  if ((preGamePrecipitation ?? 0) >= 10) score += 3;
  else if ((preGamePrecipitation ?? 0) >= 5) score += 2;
  else if ((preGamePrecipitation ?? 0) >= 2) score += 1;
  if ([65, 82, 95, 96, 99].includes(weatherCode ?? -1)) score += 2;
  if (score >= 4) return "high";
  if (score >= 2) return "moderate";
  return "low";
}

export function weatherCondition(code: number | null): string {
  if (code === null) return "Conditions unavailable";
  if (code === 0) return "Clear";
  if (code === 1) return "Mainly clear";
  if (code === 2) return "Partly cloudy";
  if (code === 3) return "Overcast";
  if (code === 45 || code === 48) return "Fog";
  if ([51, 53, 55].includes(code)) return "Drizzle";
  if (code === 56 || code === 57) return "Freezing drizzle";
  if ([61, 63, 65].includes(code)) return "Rain";
  if (code === 66 || code === 67) return "Freezing rain";
  if ([71, 73, 75, 77].includes(code)) return "Snow";
  if ([80, 81, 82].includes(code)) return "Rain showers";
  if (code === 85 || code === 86) return "Snow showers";
  if (code === 95) return "Thunderstorm";
  if (code === 96 || code === 99) return "Thunderstorm with hail";
  return "Mixed conditions";
}

function weatherSeverity(code: number | null): number {
  if (code === null || code === 0) return 0;
  if (code <= 3) return code;
  if (code === 45 || code === 48) return 4;
  if ([51, 53, 55].includes(code)) return 5;
  if ([61, 63, 65, 80, 81].includes(code)) return 6;
  if ([56, 57, 66, 67, 71, 73, 75, 77, 82, 85, 86].includes(code)) {
    return 7;
  }
  if ([95, 96, 99].includes(code)) return 8;
  return 4;
}

function minimum(values: Array<number | null>): number | null {
  const available = values.filter((value): value is number => value !== null);
  return available.length ? Math.min(...available) : null;
}

function maximum(values: Array<number | null>): number | null {
  const available = values.filter((value): value is number => value !== null);
  return available.length ? Math.max(...available) : null;
}

function localHourEnd(time: string, scheduledDate: string): number | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(time);
  if (!match || match[1] !== scheduledDate) return null;
  return Number(match[2]) * 60 + Number(match[3]);
}
